import NextAuth from 'next-auth';
import LinkedIn from 'next-auth/providers/linkedin';
import Google from 'next-auth/providers/google';
import Credentials from 'next-auth/providers/credentials';
import {userResolve} from "@/lib/service/eidr-rest-api-service";
import {getAuthorization} from "@/lib/service/eidr-proxy-service";
import {deleteAllCookies} from "@/app/actions";
import {createCompactJWT, verifyCompactJWT} from "@/lib/utils/compact-jwt";

const DEFAULT_MAX_AGE = 30 * 24 * 60 * 60 // 30 days
const now = () => (Date.now() / 1000) | 0
const enc = "A128CBC-HS256"

const providers = [
    Google(
        {
            clientId: process.env.AUTH_GOOGLE_ID,
            clientSecret: process.env.AUTH_GOOGLE_SECRET,
            async profile(profile) {
                //console.log("profile", profile);
                return {
                    ...profile,
                    image: profile.picture,
                    firstName: profile.given_name,
                    lastName: profile.family_name
                }
            }
        }
    ),
    LinkedIn(
        {
            clientId: process.env.AUTH_LINKEDIN_ID,
            clientSecret: process.env.AUTH_LINKEDIN_SECRET,
            async profile(profile) {
                //console.log("profile", profile);
                return {
                    ...profile,
                    image: profile.picture,
                    firstName: profile.given_name,
                    lastName: profile.family_name
                }
            }
        }
    ),
    Credentials({
        name: "",
        // You can specify which fields should be submitted, by adding keys to the `credentials` object.
        // e.g. domain, username, password, 2FA token, etc.
        credentials: {
            username: {},
            password: {}
        },
        authorize: async (credentials) => {

            const {username, password}: { username: string, password: string } = credentials;
            //pull the user using the web credentials
            let {response} = await userResolve(username);

            if (response?.ID) {
                //if we got a user, try to pull the user using their credentials
                const result = await userResolve(response.ID, response.ParentParty, password);

                response = result.response;
            } else {
                //Didn't get a USER
                return false;
            }

            //Got a User! Store their authorization header for all future queries
            let user = {
                name: response.ContactInfo.Name,
                email: response.ContactInfo.PrimaryEmail,
                record: response,
                authorization: await getAuthorization(response.ID, response.ParentParty, password)
            }

            return user
        }
    }),
]

export const providerMap = providers
    .map((provider) => {
        if (typeof provider === "function") {
            // @ts-ignore
            const providerData = provider()
            return {id: providerData.id, name: providerData.name}
        } else {
            return {id: provider.id, name: provider.name}
        }
    })
    .filter((provider) => provider.id !== "credentials")

export const {handlers, signIn, signOut, auth} = NextAuth({
    trustHost: true,
    providers: providers,
    basePath: '/auth',
    session: {strategy: 'jwt'},
    cookies: {
        sessionToken: {
            options: {
                domain: process.env.AUTH_DOMAIN,
                path: "/",
                httpOnly: true,
                sameSite: "lax",
                expires: null
            }
        }
    },
    callbacks: {
        authorized({request, auth}) {
            const {pathname} = request.nextUrl;
            if (pathname === '/middleware-example') return !!auth;
            return true;
        },
        jwt({token, user}) {
            //console.log("callbacks:jwt", token, user);
            if (user && user.record) {
                token.record = user.record;
                token.authorization = user.authorization;
            }

            return token;
        },
        session({session, token}) {
            //console.log("callbacks:session", session, token);
            if (token.record) {
                session.user.id = token.record.ID;
            }
            //console.log("callbacks:session", session);
            return session;
        },
    },
    jwt: {
        encode: async ({token}) => {
            return await createCompactJWT(token, process.env.AUTH_SECRET);
        },
        decode: async ({token}) => {
            const {payload, protectedHeader} = await verifyCompactJWT(token, process.env.AUTH_SECRET)

            return payload;
        },
    },
    events: {
        signOut: async (event) => {
            //console.log("events:signOut", event);
            await deleteAllCookies()
        }
    },
    experimental: {enableWebAuthn: true},
    pages: {
        signIn: "/auth/signin",
        signOut: "/auth/signout",
    }
});

/*
jwt : {
        encode: async({token}) => {
            console.log("encode", token);
            const secret = base64url.decode(process.env.AUTH_SECRET)
            console.log("secret", secret);

            let jwt = new EncryptJWT(token).setProtectedHeader({ alg: 'dir', enc: enc })
                .setIssuedAt()
                .setExpirationTime(new Date().getTime() + DEFAULT_MAX_AGE)
                .encrypt(secret)
            let jwtString = await jwt;
            console.log("encoded", jwtString);
            return await jwtString;
        },
        decode: async({token}) => {
            const secret = base64url.decode(process.env.AUTH_SECRET)
            console.log("decode", token, secret);

            const { payload, protectedHeader } = await jwtDecrypt(token,secret)
            console.log("decode", payload, protectedHeader);
            return payload;
        }
    }
 */